import {
  OrderDetail,
  OrderItem,
  OrderStatus,
} from '@epicuro-next/models/order-book';
import { getValue } from '@epicuro-next/utilities';

import { IOrderPipesMap } from '../../models';
import { getDateValue, getNumberValue } from '../get-value/get-value';

const statusValues = Object.keys(OrderStatus);

export function mapOrderDetail(
  orderDetail: OrderDetail,
  locale: string,
  pipes: IOrderPipesMap,
  groupOrderItemsProps: string[] = [],
): OrderDetail {
  const orderItems = getGroupedOrderItems(
    orderDetail.orderItems,
    groupOrderItemsProps,
  );

  return {
    ...orderDetail,
    basketId: (orderDetail.basketId ?? '').replace(/-/g, ''),
    placedDate: getDateValue(orderDetail.placedDate, locale),
    orderUpdated: getDateValue(orderDetail.orderUpdated, locale),
    netPrice: {
      ...orderDetail.netPrice,
      total: getNumberValue<number>(orderDetail.netPrice.total, pipes.number),
    },
    netPriceConfirmed: {
      ...orderDetail.netPriceConfirmed,
      total: getNumberValue<number>(
        orderDetail.netPriceConfirmed.total,
        pipes.number,
      ),
    },
    orderItems,
  };
}

export function getGroupedOrderItems(
  orderItems: OrderItem[],
  groupOrderItemsProps: string[] = [],
) {
  if (groupOrderItemsProps.length === 0) {
    return orderItems;
  }

  // UI receives flattened order items
  // They should be combined depending to groupOrderItemsProps and then flattened again inside variants property
  const groupedOrderItems = orderItems.reduce((acc, orderItem) => {
    const groupKey = groupOrderItemsProps
      .map((prop) => getValue(orderItem, prop))
      .filter(Boolean)
      .join('-');

    if (!acc[groupKey]) {
      acc[groupKey] = [orderItem];
    } else {
      acc[groupKey].push(orderItem);
    }

    return acc;
  }, {} as Record<string, OrderItem[]>);

  return Object.values(groupedOrderItems).map((group) => {
    const sortedVariants = [...group].sort(
      (a, b) =>
        statusValues.indexOf(a.salesOrderLineStatus ?? '') -
        statusValues.indexOf(b.salesOrderLineStatus ?? ''),
    );

    return {
      ...mapRootFromVariant(sortedVariants),
      variants: sortedVariants,
    };
  });
}

function mapRootFromVariant(sortedVariants: OrderItem[]): OrderItem {
  if (sortedVariants.length === 1) {
    return sortedVariants[0];
  }

  const {
    brandId,
    rootId,
    rootTitle,
    prepackInfo,
    confirmedDate,
    shippingDeliveryDate,
    estimatedTimeToArrive,
    materials,
    requestedDate,
    salesOrderLineStatus,
  } = sortedVariants[0];
  return {
    brandId,
    rootId,
    rootTitle,
    prepackInfo,
    confirmedDate,
    shippingDeliveryDate,
    estimatedTimeToArrive,
    materials,
    requestedDate,
    salesOrderLineStatus,
    invoices: Object.fromEntries(
      sortedVariants.flatMap((variant) => Object.entries(variant.invoices)),
    ),
    deliveryInfo: sortedVariants.flatMap((variant) => variant.deliveryInfo),
    ...mapPricesAndQuantities(sortedVariants),
  };
}

function mapPricesAndQuantities(orderItems: OrderItem[]) {
  const {
    discount,
    netPrice,
    netPriceConfirmed,
    consolidatedNetPrice,
    grossPrice,
    retailPrice,
  } = orderItems[0];
  const combined = orderItems.reduce(
    (acc, orderItem) => {
      acc.netPrice.total += orderItem.netPrice.total;
      acc.netPrice.unit = acc.netPrice.unit || orderItem.netPrice.unit;
      acc.netPriceConfirmed.total += orderItem.netPriceConfirmed.total;
      acc.netPriceConfirmed.unit =
        acc.netPrice.unit || orderItem.netPriceConfirmed.unit;
      acc.consolidatedNetPrice.total += orderItem.consolidatedNetPrice.total;
      acc.consolidatedNetPrice.unit =
        acc.consolidatedNetPrice.unit || orderItem.consolidatedNetPrice.unit;
      acc.grossPrice.total += orderItem.grossPrice.total;
      acc.grossPrice.unit = acc.grossPrice.unit || orderItem.grossPrice.unit;
      acc.retailPrice.total += orderItem.retailPrice.total;
      acc.retailPrice.unit = acc.retailPrice.unit || orderItem.retailPrice.unit;
      acc.confirmedQuantity += orderItem.confirmedQuantity;
      acc.requestedQuantity += orderItem.requestedQuantity;
      acc.unconfirmedQuantity += orderItem.unconfirmedQuantity;
      acc.discount = acc.discount ?? orderItem.discount;
      return acc;
    },
    {
      netPrice: { ...netPrice, total: 0 },
      netPriceConfirmed: { ...netPriceConfirmed, total: 0 },
      consolidatedNetPrice: { ...consolidatedNetPrice, total: 0 },
      grossPrice: { ...grossPrice, total: 0 },
      retailPrice: { ...retailPrice, total: 0 },
      confirmedQuantity: 0,
      requestedQuantity: 0,
      unconfirmedQuantity: 0,
      discount: {
        ...discount,
      },
    },
  );

  return {
    netPrice: combined.netPrice,
    netPriceConfirmed: combined.netPriceConfirmed,
    consolidatedNetPrice: combined.consolidatedNetPrice,
    grossPrice: combined.grossPrice,
    retailPrice: combined.retailPrice,
    discount: combined.discount,
    markup: combined.grossPrice.total
      ? combined.retailPrice.total / combined.grossPrice.total
      : 0,
    confirmedQuantity: combined.confirmedQuantity,
    requestedQuantity: combined.requestedQuantity,
    unconfirmedQuantity: combined.unconfirmedQuantity,
  };
}
